import { useEffect, useState } from "react";
import { isLoggedIn, requireAccessControl } from "../../authConfig";
import * as React from "react";
import ForwardIcon from "@mui/icons-material/Forward";
import { Tooltip, Paper, InputBase, IconButton } from "@mui/material";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    /** When true, applies the grayed-out visual style. Defaults to `disabled` if not provided. */
    showDisabledStyle?: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
}

export const QuestionInput = ({ onSend, disabled, showDisabledStyle, placeholder, clearOnSend, initQuestion }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const loginPlaceholderMessage = "Please login to continue...";
    const sendQuestionDisabledTooltip = "";
    const sendQuestionEnabledTooltip = "Ask your question";

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const onQuestionChange = (_ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const newValue = _ev.currentTarget.value;
        if (!newValue) {
            setQuestion("");
        } else if (newValue.length <= 1000) {
            setQuestion(newValue);
        }
    };

    const disableRequiredAccessControl = requireAccessControl && !isLoggedIn();

    // Apply gray style only when explicitly requested, or when access control forces disable
    // Does NOT apply when disabled only due to loading/streaming
    const applyDisabledStyle = showDisabledStyle !== undefined ? showDisabledStyle : disableRequiredAccessControl;

    // Input should only be disabled when there are access/permission issues, not during loading/streaming
    const inputDisabled = applyDisabledStyle;
    const sendQuestionDisabled = disabled || !question.trim() || disableRequiredAccessControl;

    if (disableRequiredAccessControl) {
        placeholder = loginPlaceholderMessage;
    }

    return (
        <Paper
            sx={{
                p: "2px 4px",
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "90px",
                ...(applyDisabledStyle && { backgroundColor: "#f5f5f5", opacity: 0.7, "& .MuiInputBase-input.Mui-disabled": { WebkitTextFillColor: "#444444" } })
            }}
        >
            <InputBase
                disabled={inputDisabled}
                placeholder={placeholder}
                multiline
                fullWidth
                value={question}
                onChange={onQuestionChange}
                onKeyDown={onEnterPress}
                sx={{ ml: 4, flex: 1 }}
                inputProps={{ "aria-label": placeholder }}
            />
            <Tooltip title={sendQuestionDisabled ? sendQuestionDisabledTooltip : sendQuestionEnabledTooltip}>
                <span>
                    <IconButton size="large" disabled={sendQuestionDisabled} onClick={sendQuestion} color="primary" sx={{ p: "10px" }} aria-label="directions">
                        <ForwardIcon fontSize="large" />
                    </IconButton>
                </span>
            </Tooltip>
        </Paper>
    );
};
